import React, {Component} from "react"
import {isEmpty} from "lodash"
import {Row} from "@irati/ui/src/Layout"
import moment from "moment"

import {ReportingTeamState, TeamDate, TeamReport, TeamReportDuration} from "@irati/types"
import {H3} from "@irati/ui/src/Typography"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import {hangPromise} from "@irati/utils/promise"
import reportingErrorHandler, {NO_DATA} from "@irati/admin/services/reporting/errors/reportingErrorHandler"
import Pill from "@irati/ui/src/Pill"
import Spacing from "@irati/ui/src/Spacing"
import ErrorText from "@irati/ui/src/ErrorText"
import Loading from "@irati/ui/src/Loading"
import Select from "@irati/ui/src/FormItem/Select"
import authentication from "@irati/auth"
import adminReportingService from "@irati/adminReporting/services/adminReporting"
import adminTracking, {Filter, ViewType} from "@irati-tracking/admin/admin"

import {StyledCard} from "../../../../containers/Reporting/styled"
import {ReactTable} from "../../../Table"
import {HeaderWrapper, PillWrapper} from "../../styled/styled"
import {EmptyRegions, EmptyTeams} from "../../EmptyData"
import columns from "./columns"
import {StyledCol} from "./styled"

const PAGE_SIZE = 5

interface Props extends InjectIntl {
  title: string
  isRegion?: boolean
}
interface State {
  perksByTeamReports: TeamReport[]
  page: number
  limit: number
  timeRangeOptions: TimeRangeOption[]
  timeRange?: TeamDate
  load: Promise<void>
  error: string | null
}
interface TimeRangeOption {
  value: string
  label: string
}

class PerksByTeam extends Component<Props, State> {
  state = {
    perksByTeamReports: [],
    page: 0,
    limit: PAGE_SIZE,
    timeRangeOptions: [],
    timeRange: undefined,
    load: hangPromise(),
    error: null
  }

  componentDidMount() {
    const load = this.getTeamReports().then((availableDates) => {
      const [, ...previousMonths] = availableDates
      const timeRangeOptions = previousMonths.slice(0, 12).map((value, index) => {
        return {
          label:
            index === 0
              ? this.props.formatMessage({id: "admin.components.teamEngagement.select.lastMonth"})
              : `${moment.months(value.month - 1)} '${value.year.toString().substring(2)}`,
          value: `${value.month}-${value.year}`
        }
      })
      if (timeRangeOptions?.length) {
        this.setState({timeRangeOptions, timeRange: timeRangeOptions[0]})
        return this.getTeamReports({month: previousMonths[0].month, year: previousMonths[0].year})
      }
    })
    this.setState({load})
  }

  getTeamReports = (timeRange?: TeamDate) => {
    return adminReportingService
      .getTeamReports(
        authentication.business().uuid,
        TeamReportDuration.Month,
        this.props.isRegion ? [ReportingTeamState.BillingUnit] : [ReportingTeamState.Generic],
        timeRange,
        {fetchPolicy: "network-only"}
      )
      .then((res) => {
        this.setState({perksByTeamReports: res.reports})
        return res.availableDates
      })
      .catch((err) => {
        this.setState({
          error: reportingErrorHandler(err)
        })
      })
  }

  handlePageChange = (nextPage: number) => {
    this.setState({page: nextPage})
  }

  handleSetTimeRangeUpdate = (timeRange: string) => {
    const {timeRangeOptions} = this.state

    adminTracking.reporting({
      source: "Navigation",
      type: "perks_teamCountry",
      viewType: timeRange === timeRangeOptions[0]?.value ? ViewType.default : ViewType.filtered,
      filter: Filter.date
    })
    const time = timeRange.split("-")
    const load = this.getTeamReports({month: parseInt(time[0], 10), year: parseInt(time[1], 10)})
    this.setState({load, timeRange})
  }

  render() {
    const {formatMessage, title, isRegion} = this.props
    const {page, limit, timeRangeOptions, timeRange, load, error, perksByTeamReports} = this.state

    const count = isEmpty(perksByTeamReports) ? 0 : perksByTeamReports.length
    const pages = Math.ceil(count / limit)
    const pillText = isRegion ? "admin.reporting.countryCount" : "admin.reporting.teamCount"

    const EmptyComponent = isRegion ? EmptyRegions : EmptyTeams

    return (
      <StyledCard>
        <HeaderWrapper>
          <H3>
            <FormattedMessage id={title} />
          </H3>
          {perksByTeamReports && (
            <PillWrapper>
              <Pill text={<FormattedMessage id={pillText} values={{count}} />} backgroundColor="#FBB03C" isWhiteText />
            </PillWrapper>
          )}
        </HeaderWrapper>
        {error && error !== NO_DATA ? (
          <ErrorText>{formatMessage({id: error})}</ErrorText>
        ) : (
          <Loading load={load}>
            {perksByTeamReports?.length ? (
              <>
                {timeRangeOptions.length > 0 && (
                  <Row>
                    <StyledCol xs={8}>
                      <Select
                        name="timeRangePerkRedemptions"
                        aria-label="Select Time Period"
                        options={timeRangeOptions}
                        value={timeRange}
                        onChange={this.handleSetTimeRangeUpdate}
                      />
                    </StyledCol>
                  </Row>
                )}
                <Spacing paddingLeft={1} paddingRight={1}>
                  <ReactTable
                    manual={false}
                    getPaginationProps={(allProps: any) => ({
                      ...allProps,
                      shouldScrollOnUpdate: false
                    })}
                    page={page}
                    pages={pages}
                    onPageChange={this.handlePageChange}
                    showPagination={pages > 1}
                    data={perksByTeamReports}
                    defaultPageSize={PAGE_SIZE}
                    columns={columns(formatMessage, isRegion)}
                  />
                </Spacing>
              </>
            ) : (
              <EmptyComponent />
            )}
          </Loading>
        )}
      </StyledCard>
    )
  }
}

export default injectIntl(PerksByTeam)
